import { useState, useEffect, useCallback } from 'react';

/**
 * AntiCheating component that tracks cheating attempts and can toggle prevention
 * 
 * @param {Function} onCheatingDetected - Callback function that receives cheating events
 * @returns {JSX.Element} - Notification component or null
 */
function AntiCheating({ onCheatingDetected }) {
  // Track if prevention is active - set to false by default to allow actions but still log them
  const [preventionActive, setPreventionActive] = useState(false);
  // We track if mobile device is detected but don't need to store the state
  const [, setIsMobile] = useState(false);
  // Track notification state
  const [notification, setNotification] = useState(null);

  // Function to log cheating events
  const logCheatingEvent = useCallback((eventData) => {
    // Handle both string event types and object event data
    const eventType = typeof eventData === 'string' ? eventData : eventData.type;
    const content = typeof eventData === 'object' && eventData.content ? eventData.content : null;
    
    const newEvent = {
      type: eventType,
      timestamp: new Date().toISOString(),
      preventionActive,
      content: content
    };
    // console.log('Cheating event detected:', newEvent);
    
    if (onCheatingDetected) {
      onCheatingDetected(newEvent);
    }
  }, [onCheatingDetected, preventionActive]);

  // Expose logCheatingEvent globally for Monaco Editor components
  useEffect(() => {
    window.logCheatingEvent = logCheatingEvent;
    
    // Cleanup on unmount
    return () => {
      delete window.logCheatingEvent;
    };
  }, [logCheatingEvent]);

  // Function to show notification
  const showNotification = useCallback((message) => {
    setNotification(message);
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  }, []);

  // Toggle prevention active state
  const togglePrevention = useCallback(() => {
    console.log('Toggle prevention called, current state:', preventionActive);
    setPreventionActive(prev => {
      const newState = !prev;
      console.log('Setting new prevention state to:', newState);
      showNotification(newState 
        ? "Cheating prevention is now ACTIVE" 
        : "Cheating prevention is now INACTIVE (still logging)"
      );
      return newState;
    });
  }, [showNotification, preventionActive]);

  // Check if user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
      
      if (isMobileDevice) {
        // Mobile device detected - track it
        logCheatingEvent('mobile-device-access');
      }
    };
    
    checkMobile();
  }, [logCheatingEvent]);

  // No initial notification about the shortcut

  // Add a dedicated effect for the toggle shortcut
  useEffect(() => {
    const handleToggleShortcut = (e) => {
      // Toggle prevention with Shift+Control+X (Windows) or Shift+Command+X (Mac)
      if (e.shiftKey && (e.ctrlKey || e.metaKey) && (e.key === 'x' || e.key === 'X')) {
        console.log('Toggle shortcut detected in dedicated handler!');
        e.preventDefault();
        togglePrevention();
      }
    };

    // Add a global event listener for the toggle shortcut
    window.addEventListener('keydown', handleToggleShortcut);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleToggleShortcut);
    };
  }, [togglePrevention]);

  // Set up anti-cheating event listeners
  useEffect(() => {
    // Handle copy events - always log, only prevent if active
    const handleCopy = (e) => {
      // Get the selected text that's being copied
      const selectedText = window.getSelection().toString();
      logCheatingEvent({
        type: 'copy-attempt',
        content: selectedText
      });
      if (preventionActive) {
        e.preventDefault();
      }
    };
    
    // Handle paste events - always log, only prevent if active
    const handlePaste = (e) => {
      // Get the pasted content from clipboard if available
      let pastedContent = '';
      if (e.clipboardData && e.clipboardData.getData) {
        pastedContent = e.clipboardData.getData('text/plain');
      }
      logCheatingEvent({
        type: 'paste-attempt',
        content: pastedContent
      });
      if (preventionActive) {
        e.preventDefault();
      }
    };
    
    // Handle right click - always log, only prevent if active
    const handleRightClick = (e) => {
      logCheatingEvent('right-click-attempt');
      if (preventionActive) {
        e.preventDefault();
      }
    };
    
    
    // Track window focus - always log
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logCheatingEvent('tab-switch');
      }
    };
    
    // Track window blur - always log
    const handleWindowBlur = () => {
      logCheatingEvent('window-blur');
    };
    
    // Handle keyboard shortcuts
    const handleKeyDown = (e) => {
      // Note: Toggle shortcut is handled in a separate dedicated event listener
      
      // Always log these events
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        logCheatingEvent('print-attempt');
        if (preventionActive) {
          e.preventDefault();
        }
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        logCheatingEvent('save-page-attempt');
        if (preventionActive) {
          e.preventDefault();
        }
      }
      
      if ((e.ctrlKey && e.shiftKey && e.key === 'i') || e.key === 'F12') {
        logCheatingEvent('inspect-element-attempt');
        if (preventionActive) {
          e.preventDefault();
        }
      }
      
      // Detect screen recording shortcuts (varies by OS)
      if ((e.metaKey && e.shiftKey && e.key === '5') || 
          (e.key === 'g' && e.getModifierState('Meta')) ||
          (e.key === 'r' && e.altKey && e.getModifierState('Meta'))) {
        logCheatingEvent('screen-recording-attempt');
        if (preventionActive) {
          e.preventDefault();
        }
      }
    };
    
    // Detect DevTools
    const detectDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      
      if (widthThreshold || heightThreshold) {
        logCheatingEvent('devtools-open');
      }
    };

    // Add event listeners
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('contextmenu', handleRightClick);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('keydown', handleKeyDown);
    
    // Check for DevTools periodically (every x seconds to reduce log frequency)
    const devToolsInterval = setInterval(detectDevTools, 15000);
    
    // CSS to prevent selection - only apply if prevention is active
    let style = null;
    if (preventionActive) {
      style = document.createElement('style');
      style.textContent = `
        body {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        
        input, textarea {
          -webkit-user-select: auto;
          -moz-user-select: auto;
          -ms-user-select: auto;
          user-select: auto;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Cleanup function
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('contextmenu', handleRightClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('keydown', handleKeyDown);
      clearInterval(devToolsInterval);
      if (style) {
        document.head.removeChild(style);
      }
    };
  }, [logCheatingEvent, preventionActive, togglePrevention]);

  // Render notification if active
  return (
    <>
      {notification && (
        <div className="fixed top-20 left-0 right-0 z-50 flex justify-center">
          <div className={`px-4 py-2 rounded-md shadow-md ${preventionActive ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            <div className="flex items-center">
              {preventionActive ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              <span className="font-medium">{notification}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AntiCheating;
